import React, { useState, useRef, useMemo } from "react";
import { CCard, CCardHeader, CCardBody, CButtonGroup, CButton, CSpinner } from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilSave, cilActionUndo, cilTrash } from "@coreui/icons";
import { useTranslation } from "react-i18next";
import { getInsertionIndex } from "../../utils/cameraUtils";

const RoiEditor = ({ snapshotUrl, polygonPoints, setPolygonPoints, isLive, showRoi, onSave, onReset, onClear, cameraName, onImageLoad }) => {
  const { t } = useTranslation();
  const imgRef = useRef(null);
  const svgRef = useRef(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [displayedSize, setDisplayedSize] = useState({ width: 0, height: 0 });
  const [draggingPoint, setDraggingPoint] = useState(null);

  const isModified = useMemo(() => {
    if (!polygonPoints || !polygonPoints.initial || !polygonPoints.current) return false;
    return JSON.stringify(polygonPoints.initial) !== JSON.stringify(polygonPoints.current);
  }, [polygonPoints]);

  const handleImageLoad = () => {
    if (imgRef.current) {
      const { naturalWidth, naturalHeight, clientWidth, clientHeight } = imgRef.current;
      setImageSize({ width: naturalWidth, height: naturalHeight });
      setDisplayedSize({ width: clientWidth, height: clientHeight });
      onImageLoad({ naturalWidth, naturalHeight });
    }
  };

  const handleCanvasMouseDown = (e) => {
    if (!showRoi || isLive || !svgRef.current || imageSize.width === 0 || displayedSize.width === 0) return;
    e.preventDefault();
    const rect = svgRef.current.getBoundingClientRect();
    const scaleX = imageSize.width / displayedSize.width;
    const scaleY = imageSize.height / displayedSize.height;
    const newPoint = { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
    const currentPoints = polygonPoints.current || [];
    const insertIndex = getInsertionIndex(newPoint, currentPoints);
    const newPointsList = [...currentPoints];
    newPointsList.splice(insertIndex, 0, newPoint);
    setPolygonPoints({ ...polygonPoints, current: newPointsList });
    setDraggingPoint(insertIndex);
  };

  const handlePointMouseDown = (e, index) => {
    e.stopPropagation(); e.preventDefault();
    if (!showRoi || isLive) return;
    setDraggingPoint(index);
  };

  const handleSvgMouseMove = (e) => {
    if (draggingPoint === null || !svgRef.current) return;
    e.preventDefault();
    const rect = svgRef.current.getBoundingClientRect();
    const scaleX = imageSize.width / displayedSize.width;
    const scaleY = imageSize.height / displayedSize.height;
    const x = Math.max(0, Math.min(imageSize.width, (e.clientX - rect.left) * scaleX));
    const y = Math.max(0, Math.min(imageSize.height, (e.clientY - rect.top) * scaleY));
    const newPoints = [...polygonPoints.current];
    newPoints[draggingPoint] = { x, y };
    setPolygonPoints({ ...polygonPoints, current: newPoints });
  };

  const handleSvgMouseUp = () => setDraggingPoint(null);
  const handlePointRightClick = (e, indexToDelete) => {
    e.preventDefault(); e.stopPropagation();
    if (!showRoi || isLive) return;
    setPolygonPoints({ ...polygonPoints, current: polygonPoints.current.filter((_, index) => index !== indexToDelete) });
  };

  const pointsToRender = polygonPoints?.current || [];

  return (
    <CCard className="mb-4">
      <CCardHeader>{t("camera.title_roi")} <strong>{cameraName ? `- ${cameraName}` : ""}</strong></CCardHeader>
      <CCardBody>
        {snapshotUrl ? (
          <>
            <div style={{ position: "relative", cursor: isLive || !showRoi ? "default" : "crosshair", display: "inline-block", width: "100%" }}>
              <img ref={imgRef} src={snapshotUrl} alt="Snapshot" style={{ width: "100%", height: "auto", display: "block", pointerEvents: "none" }} onLoad={handleImageLoad} />
              <svg ref={svgRef} width="100%" height="100%" viewBox={`0 0 ${imageSize.width} ${imageSize.height}`} style={{ position: "absolute", top: 0, left: 0, pointerEvents: imageSize.width > 0 ? "auto" : "none" }} onMouseDown={handleCanvasMouseDown} onMouseMove={handleSvgMouseMove} onMouseUp={handleSvgMouseUp} onMouseLeave={handleSvgMouseUp}>
                {showRoi && pointsToRender.length > 0 && <polygon points={pointsToRender.map((p) => `${p.x},${p.y}`).join(" ")} style={{ fill: "rgba(255, 0, 0, 0.3)", stroke: "red", strokeWidth: 4, vectorEffect: "non-scaling-stroke", pointerEvents: "none" }} />}
                {showRoi && pointsToRender.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="10" fill="white" stroke="red" strokeWidth="2" onMouseDown={(e) => handlePointMouseDown(e, i)} onContextMenu={(e) => handlePointRightClick(e, i)} style={{ cursor: isLive ? "not-allowed" : "move", vectorEffect: "non-scaling-stroke" }} />)}
              </svg>
            </div>
            {showRoi && (
              <CButtonGroup className="mt-3 w-100">
                <CButton color="primary" onClick={onSave} disabled={!isModified || isLive}><CIcon icon={cilSave} className="me-2" /> {t("common.save")}</CButton>
                <CButton color="secondary" onClick={onReset} disabled={!isModified || isLive}><CIcon icon={cilActionUndo} className="me-2" /> {t("common.reset")}</CButton>
                <CButton color="danger" variant="outline" onClick={onClear} disabled={isLive || pointsToRender.length === 0}><CIcon icon={cilTrash} className="me-2" /> {t("common.clear")}</CButton>
              </CButtonGroup>
            )}
            <small className="form-text text-muted mt-2 d-block">{t("camera.roi_hint")}</small>
          </>
        ) : (<div className="text-center p-3"><CSpinner /></div>)}
      </CCardBody>
    </CCard>
  );
};
export default RoiEditor;